// DEPLOY: /var/www/woody.wtf/frontend/src/chart/AlertPanel.js
// Settings for one alert line: direction, what counts (close / touch), what happens after it
// fires, the note sent with the alert, on/off, and Re-arm. Shows the end-of-day job's status.
// Desktop: panel beside the chart. Phone: slide-up sheet.
import React from 'react';
import { ALERT_DEFAULT, alertStatus } from './drawings';

const S = {
  panel:  { position: 'absolute', top: '80px', right: '72px', width: '300px', maxHeight: 'calc(100% - 116px)', overflowY: 'auto', zIndex: 17, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)', padding: '10px 12px' },
  sheet:  { position: 'fixed', left: 0, right: 0, bottom: 0, maxHeight: '75vh', overflowY: 'auto', zIndex: 41, background: 'var(--surface)', borderRadius: '14px 14px 0 0', boxShadow: '0 -4px 24px rgba(0,0,0,0.15)', padding: '12px 14px 20px' },
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 40 },
  head:   { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' },
  title:  { fontSize: '13px', fontWeight: 700, color: 'var(--text)' },
  close:  { border: 'none', background: 'none', fontSize: '16px', color: 'var(--text-muted)', cursor: 'pointer' },
  status: { fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px' },
  sub:    { fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)', fontFamily: "'DM Mono',monospace", textTransform: 'uppercase', letterSpacing: '0.06em', margin: '10px 0 4px' },
  seg:    { display: 'flex', border: '1px solid var(--border)', borderRadius: '6px', overflow: 'hidden' },
  segBtn: { flex: 1, padding: '7px 4px', border: 'none', borderRight: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text-muted)', fontSize: '12px', cursor: 'pointer' },
  note:   { width: '100%', minHeight: '60px', padding: '6px 8px', fontSize: '13px', fontFamily: "'DM Sans',sans-serif", border: '1px solid var(--border)', borderRadius: '6px', background: 'var(--bg)', color: 'var(--text)', boxSizing: 'border-box', resize: 'vertical' },
  row:    { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginTop: '12px' },
  check:  { width: '18px', height: '18px', accentColor: 'var(--accent)', margin: 0 },
  btn:    { padding: '6px 12px', fontSize: '12px', fontWeight: 600, border: '1px solid var(--accent)', borderRadius: '6px', background: 'none', color: 'var(--accent)', cursor: 'pointer' },
};

const STATUS = {
  new:     'Waiting for its first end-of-day check.',
  armed:   'Armed - checked after each close.',
  fired:   'Fired - press Re-arm to watch it again.',
  expired: 'Expired - the candles have passed point B.',
  error:   'Problem: ',
  off:     'Switched off - not checked.',
};

function Seg({ value, options, onChange }) {
  return (
    <div style={S.seg}>
      {options.map(([v, label], i) => (
        <button key={v} onClick={() => onChange(v)}
                style={{ ...S.segBtn, ...(i === options.length - 1 ? { borderRight: 'none' } : {}),
                         ...(value === v ? { background: 'var(--accent)', color: '#fff' } : {}) }}>{label}</button>
      ))}
    </div>
  );
}

export default function AlertPanel({ drawing, mobile, onChange, onClose }) {
  if (!drawing) return null;
  const al = { ...ALERT_DEFAULT, ...(drawing.alert || {}) };
  const set = patch => onChange({ alert: { ...al, ...patch } });
  const st = alertStatus(drawing);
  const s = drawing.state || {};

  const body = (
    <div style={mobile ? S.sheet : S.panel} onPointerDown={e => e.stopPropagation()}>
      <div style={S.head}>
        <span style={S.title}>🔔 Alert line <a href="/help#alert-lines" target="_blank" rel="noopener noreferrer"
          title="When do alert lines fire?" style={{ fontSize: '11px', color: 'var(--accent)', textDecoration: 'none', marginLeft: '4px' }}>?</a></span>
        <button style={S.close} onClick={onClose} title="Close">✕</button>
      </div>
      <div style={S.status}>
        {STATUS[st] || ''}{st === 'error' ? s.message || '' : ''}
        {s.last_fired_d ? ` Last fired ${s.last_fired_d}.` : ''}
      </div>
      <div style={S.sub}>Alert when price is</div>
      <Seg value={al.dir} onChange={v => set({ dir: v })} options={[['above', '▲ Above the line'], ['below', '▼ Below the line']]} />
      <div style={S.sub}>Counts when</div>
      <Seg value={al.trigger} onChange={v => set({ trigger: v })}
           options={[['close', 'Closes past it'], ['touch', al.dir === 'below' ? 'Low touches it' : 'High touches it']]} />
      <div style={S.sub}>After it fires</div>
      <Seg value={al.after} onChange={v => set({ after: v })}
           options={[['once', 'Once, then stop'], ['rearm', 'Again after crossing back'], ['daily', 'Every day past it']]} />
      <div style={S.sub}>Note (sent with the alert)</div>
      <textarea style={S.note} value={al.note} placeholder="e.g. Ascending triangle breakout"
                onChange={e => set({ note: e.target.value })} />
      <div style={S.row}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
          <input type="checkbox" style={S.check} checked={al.armed !== false} onChange={e => set({ armed: e.target.checked })} /> Alert on
        </label>
        {(st === 'fired' || st === 'expired') && <button style={S.btn} onClick={() => set({ rev: (al.rev || 0) + 1, armed: true })}>Re-arm</button>}
      </div>
    </div>
  );
  return mobile ? <><div style={S.backdrop} onClick={onClose} />{body}</> : body;
}
